// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file CollapsibleGroupContext.tsx
 * @input Uses React createContext
 * @output Exports CollapsibleGroupContext, CollapsibleGroupContextValue,
 *   CollapsibleGroupPresentationContext, CollapsibleGroupPresentationValue,
 *   and CollapsibleGroupDensity types
 * @position Context definitions for collapsible group coordination and presentation
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Collapsible/CollapsibleGroup.tsx (provider)
 * - /packages/core/src/Collapsible/Collapsible.doc.mjs
 * - /packages/cli/assets/templates/blocks/components/Collapsible/ (showcase blocks)
 */
import { createContext } from 'react';

/**
 * Context value provided by CollapsibleGroup to coordinate collapsible children.
 */

/**
 * Context for collapsible group coordination.
 * When present, collapsible components defer their open/close state to the group.
 */
export const CollapsibleGroupContext = /*#__PURE__*/createContext(null);
CollapsibleGroupContext.displayName = 'CollapsibleGroupContext';

/**
 * Row density for the items of a CollapsibleGroup, controlling trigger and
 * content block padding. Shares the repo-wide density vocabulary
 * (Table, List, Item).
 */

/**
 * Presentation value provided by CollapsibleGroup so each Collapsible can
 * draw its own group chrome (StyleX has no child selectors, so the group
 * cannot style items from the outside).
 */

/**
 * Context for collapsible group presentation (dividers, density).
 * Kept separate from CollapsibleGroupContext so the public useCollapsible
 * state API is unaffected. Collapsible resets this context around its
 * children so nested collapsibles never inherit row chrome.
 */
export const CollapsibleGroupPresentationContext = /*#__PURE__*/createContext(null);
CollapsibleGroupPresentationContext.displayName = 'CollapsibleGroupPresentationContext';